import { Injectable, inject } from '@angular/core';
import { DOCUMENT } from '@angular/common';

export interface OrganizationSchema {
  name: string;
  url: string;
  logo?: string;
  description?: string;
  contactPoint?: {
    contactType: string;
    email?: string;
    telephone?: string;
  };
  sameAs?: string[];
}

export interface WebSiteSchema {
  name: string;
  url: string;
  description?: string;
  potentialAction?: {
    '@type': string;
    target: {
      '@type': string;
      urlTemplate: string;
    };
    'query-input': string;
  };
}

export interface BreadcrumbItem {
  name: string;
  url: string;
}

export interface CourseSchema {
  name: string;
  description: string;
  provider: {
    name: string;
    type: string;
  };
  educationalLevel?: string;
  url?: string;
}

@Injectable({
  providedIn: 'root',
})
export class StructuredDataService {
  private readonly document = inject(DOCUMENT) as Document;

  /**
   * Inject Organization schema (JSON-LD)
   */
  injectOrganizationSchema(data: OrganizationSchema): void {
    const schema = {
      '@context': 'https://schema.org',
      '@type': 'Organization',
      name: data.name,
      url: data.url,
      ...(data.logo && { logo: data.logo }),
      ...(data.description && { description: data.description }),
      ...(data.contactPoint && {
        contactPoint: {
          '@type': 'ContactPoint',
          contactType: data.contactPoint.contactType,
          ...(data.contactPoint.email && { email: data.contactPoint.email }),
          ...(data.contactPoint.telephone && { telephone: data.contactPoint.telephone }),
        },
      }),
      ...(data.sameAs && data.sameAs.length > 0 && { sameAs: data.sameAs }),
    };

    this.injectJSONLD('organization', schema);
  }

  /**
   * Inject WebSite schema with search action (JSON-LD)
   */
  injectWebSiteSchema(data: WebSiteSchema): void {
    const schema: any = {
      '@context': 'https://schema.org',
      '@type': 'WebSite',
      name: data.name,
      url: data.url,
      ...(data.description && { description: data.description }),
    };

    if (data.potentialAction) {
      schema.potentialAction = {
        '@type': data.potentialAction['@type'],
        target: {
          '@type': data.potentialAction.target['@type'],
          urlTemplate: data.potentialAction.target.urlTemplate,
        },
        'query-input': data.potentialAction['query-input'],
      };
    }

    this.injectJSONLD('website', schema);
  }

  /**
   * Inject BreadcrumbList schema (JSON-LD)
   */
  injectBreadcrumbSchema(items: BreadcrumbItem[]): void {
    const schema = {
      '@context': 'https://schema.org',
      '@type': 'BreadcrumbList',
      itemListElement: items.map((item, index) => ({
        '@type': 'ListItem',
        position: index + 1,
        name: item.name,
        item: item.url,
      })),
    };

    this.injectJSONLD('breadcrumb', schema);
  }

  /**
   * Inject Course schema (JSON-LD)
   */
  injectCourseSchema(data: CourseSchema): void {
    const schema: any = {
      '@context': 'https://schema.org',
      '@type': 'Course',
      name: data.name,
      description: data.description,
      provider: {
        '@type': data.provider.type,
        name: data.provider.name,
      },
      ...(data.educationalLevel && { educationalLevel: data.educationalLevel }),
      ...(data.url && { url: data.url }),
    };

    this.injectJSONLD('course', schema);
  }

  /**
   * Inject SoftwareApplication schema (JSON-LD)
   */
  injectSoftwareApplicationSchema(data: {
    name: string;
    applicationCategory: string;
    operatingSystem: string;
    description?: string;
    url?: string;
    offers?: {
      price: string;
      priceCurrency: string;
    };
  }): void {
    const schema: any = {
      '@context': 'https://schema.org',
      '@type': 'SoftwareApplication',
      name: data.name,
      applicationCategory: data.applicationCategory,
      operatingSystem: data.operatingSystem,
      ...(data.description && { description: data.description }),
      ...(data.url && { url: data.url }),
      ...(data.offers && {
        offers: {
          '@type': 'Offer',
          price: data.offers.price,
          priceCurrency: data.offers.priceCurrency,
        },
      }),
    };

    this.injectJSONLD('software', schema);
  }

  /**
   * Remove a specific JSON-LD script by id
   */
  removeJSONLD(id: string): void {
    const script = this.document.getElementById(`jsonld-${id}`);
    if (script) {
      script.remove();
    }
  }

  /**
   * Remove all JSON-LD scripts
   */
  removeAllJSONLD(): void {
    const scripts = this.document.querySelectorAll('script[type="application/ld+json"]');
    scripts.forEach((script: Element) => script.remove());
  }

  /**
   * Inject JSON-LD script into the document head
   */
  private injectJSONLD(id: string, schema: object): void {
    // Remove existing script with same id
    this.removeJSONLD(id);

    // Create new script element
    const script = this.document.createElement('script');
    script.id = `jsonld-${id}`;
    script.type = 'application/ld+json';
    script.text = JSON.stringify(schema);
    this.document.head.appendChild(script);
  }
}

